import React, { useState, useEffect, useCallback } from 'react';
import { projectsService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Textarea } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  FolderKanban,
  Plus,
  Search,
  Edit2,
  Trash2,
} from 'lucide-react';

export const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  const { success, error: toastError } = useToast();

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsService.list({ search });
      if (res && res.data) {
        setProjects(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar proyectos.');
    } finally {
      setLoading(false);
    }
  }, [search, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProjects();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchProjects]);

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({ name: '', description: '' });
    setErrors({});
    setModalOpen(true);
  };

  const openEditModal = (p) => {
    setEditingProject(p);
    setFormData({
      name: p.name || '',
      description: p.description || '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      if (editingProject) {
        await projectsService.update(editingProject.id, formData);
        success('Proyecto actualizado correctamente.');
      } else {
        await projectsService.create(formData);
        success('Proyecto creado exitosamente.');
      }
      setModalOpen(false);
      fetchProjects();
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar proyecto.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    setSubmitting(true);
    try {
      await projectsService.delete(projectToDelete.id);
      success('Proyecto eliminado correctamente.');
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar proyecto.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <FolderKanban className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Gestión de Proyectos
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Organiza y categoriza tus facturas y presupuestos por proyectos de clientes
          </p>
        </div>

        <Button onClick={openCreateModal} icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
          Nuevo Proyecto
        </Button>
      </div>

      {/* List Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3 w-full sm:w-80">
            <Input
              placeholder="Buscar proyecto por nombre..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando proyectos..." className="py-12" />
          ) : projects.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title="No hay proyectos registrados"
              description={search ? "No se encontraron proyectos para tu búsqueda." : "Crea tu primer proyecto para agrupar servicios y comprobantes fiscales."}
              actionLabel="Crear Proyecto"
              onAction={openCreateModal}
              actionIcon={Plus}
            />
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {projects.map((p) => (
                <div
                  key={p.id}
                  className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      <FolderKanban className="w-4 h-4 text-[#08707A] dark:text-teal-400" />
                      {p.name}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {p.description || 'Sin descripción adicional.'}
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 self-end sm:self-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Edit2}
                      onClick={() => openEditModal(p)}
                      title="Editar proyecto"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => {
                        setProjectToDelete(p);
                        setDeleteConfirmOpen(true);
                      }}
                      title="Eliminar proyecto"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title={editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
        subtitle="Agrupa comprobantes fiscales bajo un mismo centro de costos o cliente"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Nombre del Proyecto"
            placeholder="Ej: Rediseño Web Corporativo 2026"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={errors.name?.[0]}
            required
            autoFocus
          />

          <Textarea
            label="Descripción o Alcance"
            placeholder="Detalles sobre el proyecto, entregables o cliente..."
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            error={errors.description?.[0]}
            rows={3}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="bg-[#08707A] hover:bg-[#065B63]"
            >
              {editingProject ? 'Guardar Cambios' : 'Crear Proyecto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar proyecto?"
        message={`¿Estás seguro de que deseas eliminar "${projectToDelete?.name}"?`}
        confirmText="Eliminar Proyecto"
        isLoading={submitting}
      />
    </div>
  );
};
